import { Box, Button, Grid, MenuItem, Select, Typography } from "@mui/material";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { ReactComponent as Upload } from "../../../../../assets/AccountingModule/upload.svg";
import color from "../../../../../constants/color";
import style from "./style";
import { useAlert } from "../../../../../context/AlertContext";

function ImportStatementPage({
  handleUpload,
  loading,
  error,
  success,
}) {
  const encodingOptions = [
    { label: "UTF-8", value: "utf-8" },
    { label: "ISO-8859-1", value: "iso-8859-1" },
    { label: "Windows-1252", value: "windows-1252" },
    { label: "ASCII", value: "ascii" },
  ];

  const [uploadedFile, setUploadedFile] = useState(null);
  const [encoding, setEncoding] = useState("utf-8");
  const { showAlert } = useAlert();

  const handleFileDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Check for allowed file types
    const allowedExtensions = [".csv", ".ofx", ".qif", ".txt", ".xlsx", ".pdf"];
    const allowedMimeTypes = [
      "text/csv",
      "application/vnd.ms-excel", // some CSVs are detected as this
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
      "application/pdf",
      "text/plain", // txt files
      "application/x-ofx", // OFX files
      "application/x-qif", // QIF files
    ];

    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf("."));
    const isValidExtension = allowedExtensions.includes(fileExtension);
    const isValidMimeType = allowedMimeTypes.includes(file.type);

    if (!isValidExtension && !isValidMimeType) {
      showAlert(
        "Please upload a CSV, OFX, QIF, TXT, XLSX, or PDF file only.",
        "warning"
      );
      return;
    }

    setUploadedFile(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileDrop,
    multiple: false,
  });

  const handleUploadFile = async () => {
    await handleUpload(uploadedFile, encoding);
    setUploadedFile(null);
    setEncoding("utf-8");
  };

  return (
    <Box px="15px" pt="20px">
      <form onSubmit={() => {}}>
        <Typography sx={style.heading}>Import Statements</Typography>
        <Box>
          <Grid container>
            <Grid item xs={12} sx={style.card}>
              <Box sx={style.inputSection}>
                <Box sx={style.inputBox}>
                  <Typography sx={style.inputText}>
                    Character Encoding
                  </Typography>
                  <Select
                    value={encoding}
                    onChange={(e) => setEncoding(e.target.value)}
                    name="Encoding"
                    sx={style.encodingInput}
                    fullWidth
                  >
                    {encodingOptions.map((opt, index) => (
                      <MenuItem
                        key={index}
                        value={opt.value}
                        sx={style.dropDownMenu}
                      >
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>

              <Box sx={style.uploadSection}>
                <Typography sx={style.uploadText}>Upload Statements</Typography>
                <Box {...getRootProps()} sx={style.dragAndDrop}>
                  <input {...getInputProps()} />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={style.chooseIconSection}>
                      <Box sx={{ mr: 1 }}>
                        <Upload />
                      </Box>
                      <Typography sx={style.choose}>Choose File </Typography>
                    </Box>
                    {isDragActive
                      ? "Drop the file here..."
                      : "or drag and drop a file here"}
                  </Box>
                </Box>
              </Box>
              {/* Show selected file info */}
              {uploadedFile && (
                <Box
                  sx={{ mt: 1, mb: 1, display: "flex", alignItems: "center" }}
                >
                  <Typography sx={{ fontSize: 14, color: color.primaryText }}>
                    Selected File: <b>{uploadedFile.name}</b> (
                    {(uploadedFile.size / 1024).toFixed(2)} KB)
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "end",
                  width: "100%",
                  alignItems: "center",
                }}
              >
                <Button
                  variant="contained"
                  color="success"
                  sx={{ ...style.saveBtn, mt: 2 }}
                  onClick={handleUploadFile}
                  disabled={loading || !uploadedFile}
                >
                  {loading ? "Uploading..." : "Save"}
                </Button>
              </Box>
              {/* Show error or success message */}
              {error && (
                <Typography color="error" sx={{ mt: 2 }}>
                  {error}
                </Typography>
              )}
              {success && (
                <Typography color="success.main" sx={{ mt: 2 }}>
                  {success}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>
      </form>
    </Box>
  );
}

export default ImportStatementPage;
